import React, { Fragment } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Disclosure, Menu, Transition } from '@headlessui/react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { MoonIcon, SunIcon, Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';

const navigation = [
    { name: 'DASHBOARD', href: '/' },
    { name: 'LIVE', href: '/active-session' },
    { name: 'SESSIONS', href: '/sessions' },
    { name: 'FINANCE', href: '/finance' },
    { name: 'STATISTICS', href: '/statistics' },
    { name: 'HANDS', href: '/hands' },
    { name: 'LEAKS', href: '/leaks' },
    { name: 'PLAYERS', href: '/players' },
    { name: 'LOCATIONS', href: '/locations' },
];

function Header() {
    const { user, logout } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const location = useLocation();

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Disclosure as="nav" className={`
                bg-[#faf8f5] dark:bg-gray-800
                border border-gray-200 dark:border-gray-700
                rounded-lg
                shadow-sm
                my-4
            `}>
                {({ open }) => (
                    <div className="mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="relative flex h-16 sm:h-20 lg:h-24 items-center justify-between">
                            {/* Logo */}
                            <div className="flex-shrink-0">
                                <Link to="/" className="flex items-center">
                                    <img
                                        className="h-12 w-auto sm:h-16 lg:h-20"
                                        src={theme === 'dark' ? '/pokerium_logo.webp' : '/pokerium_logo_light.png'}
                                        alt="Pokerium"
                                    />
                                </Link>
                                <span className="ml-1 text-[10px] text-gray-400 dark:text-gray-500">v2.4.0</span>
                            </div>

                            {/* Desktop Navigation */}
                            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2">
                                <div className="flex space-x-2 lg:space-x-3">
                                    {navigation.map((item) => {
                                        const isActive = location.pathname === item.href;
                                        return (
                                            <Link
                                                key={item.name}
                                                to={item.href}
                                                className={`
                                                    px-3 py-2 lg:px-4 lg:py-2.5
                                                    rounded-lg
                                                    text-xs lg:text-sm font-medium
                                                    transition-all duration-200
                                                    ${isActive ? 
                                                        'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300' : 
                                                        'bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300'
                                                    }
                                                    hover:bg-primary-50 dark:hover:bg-primary-900/20
                                                    hover:text-primary-600 dark:hover:text-primary-400
                                                    hover:scale-105
                                                    active:scale-95
                                                `}
                                            >
                                                {item.name}
                                            </Link>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Right side controls */}
                            <div className="flex items-center space-x-2 sm:space-x-4">
                                <button
                                    onClick={toggleTheme}
                                    aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                                    className="p-2 sm:p-2.5 rounded-lg bg-gray-50 dark:bg-gray-700/50
                                             text-gray-600 hover:text-gray-900
                                             dark:text-gray-400 dark:hover:text-white
                                             hover:bg-gray-100 dark:hover:bg-gray-700
                                             transition-colors duration-200"
                                >
                                    {theme === 'dark' ? (
                                        <SunIcon className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
                                    ) : (
                                        <MoonIcon className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
                                    )}
                                </button>
                                
                                {/* Mobile menu button */}
                                <div className="md:hidden">
                                    <Disclosure.Button className="p-2 rounded-lg bg-gray-50 dark:bg-gray-700/50
                                                                 text-gray-600 hover:text-gray-900 
                                                                 dark:text-gray-400 dark:hover:text-white
                                                                 hover:bg-gray-100 dark:hover:bg-gray-700
                                                                 transition-colors duration-200">
                                        <span className="sr-only">Open main menu</span>
                                        {open ? (
                                            <XMarkIcon className="h-5 w-5" />
                                        ) : (
                                            <Bars3Icon className="h-5 w-5" />
                                        )}
                                    </Disclosure.Button>
                                </div>

                                {/* Desktop user menu */}
                                {user && (
                                    <div className="hidden md:block">
                                        <Menu as="div" className="relative">
                                            <Menu.Button className="flex items-center text-sm px-3 py-2 lg:px-4 lg:py-2.5 
                                                                  rounded-lg bg-gray-50 dark:bg-gray-700/50
                                                                  hover:bg-gray-100 dark:hover:bg-gray-700
                                                                  transition-colors duration-200">
                                                <span className="sr-only">Open user menu</span>
                                                <span className="text-gray-700 dark:text-gray-300 text-xs lg:text-sm">
                                                    {user.username}
                                                </span>
                                            </Menu.Button>
                                            <Transition
                                                as={Fragment}
                                                enter="transition ease-out duration-100"
                                                enterFrom="transform opacity-0 scale-95"
                                                enterTo="transform opacity-100 scale-100"
                                                leave="transition ease-in duration-75"
                                                leaveFrom="transform opacity-100 scale-100"
                                                leaveTo="transform opacity-0 scale-95"
                                            >
                                                <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right
                                                                     rounded-lg bg-white dark:bg-gray-800 shadow-lg
                                                                     border border-gray-200 dark:border-gray-700
                                                                     focus:outline-none">
                                                    <Menu.Item>
                                                        {({ active }) => (
                                                            <Link
                                                                to="/break-settings"
                                                                className={`${
                                                                    active ? 'bg-gray-100 dark:bg-gray-700' : ''
                                                                } block px-4 py-2 text-sm
                                                                 text-gray-700 dark:text-gray-300 rounded-t-lg`}
                                                            >
                                                                Break Settings
                                                            </Link>
                                                        )}
                                                    </Menu.Item>
                                                    <Menu.Item>
                                                        {({ active }) => (
                                                            <button
                                                                onClick={logout}
                                                                className={`${
                                                                    active ? 'bg-gray-100 dark:bg-gray-700' : ''
                                                                } block w-full text-left px-4 py-2 text-sm
                                                                 text-gray-700 dark:text-gray-300 rounded-b-lg`}
                                                            >
                                                                Sign out
                                                            </button>
                                                        )}
                                                    </Menu.Item>
                                                </Menu.Items>
                                            </Transition>
                                        </Menu>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Mobile Navigation */}
                        <Disclosure.Panel className="md:hidden">
                            <div className="px-2 pt-2 pb-3 space-y-1">
                                {navigation.map((item) => {
                                    const isActive = location.pathname === item.href;
                                    return (
                                        <Disclosure.Button
                                            key={item.name}
                                            as={Link}
                                            to={item.href}
                                            className={`
                                                block px-3 py-2 rounded-lg text-base font-medium
                                                transition-colors duration-200
                                                ${isActive ? 
                                                    'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300' : 
                                                    'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                                }
                                            `}
                                        >
                                            {item.name}
                                        </Disclosure.Button>
                                    );
                                })}
                                
                                {/* Mobile user menu */}
                                {user && (
                                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                                        <div className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
                                            Signed in as <span className="font-medium">{user.username}</span>
                                        </div>
                                        <Disclosure.Button
                                            as={Link}
                                            to="/break-settings"
                                            className="block w-full text-left px-3 py-2 text-sm
                                                     text-gray-700 dark:text-gray-300 hover:bg-gray-100
                                                     dark:hover:bg-gray-700 rounded-lg transition-colors duration-200"
                                        >
                                            Break Settings
                                        </Disclosure.Button>
                                        <button
                                            onClick={logout}
                                            className="block w-full text-left px-3 py-2 text-sm
                                                     text-gray-700 dark:text-gray-300 hover:bg-gray-100
                                                     dark:hover:bg-gray-700 rounded-lg transition-colors duration-200"
                                        >
                                            Sign out
                                        </button>
                                    </div>
                                )}
                            </div>
                        </Disclosure.Panel>
                    </div>
                )}
            </Disclosure>
        </div>
    );
}

export default Header; 